/* =====================================================================
   LA FICHA PREVIA, SIN PANTALLA · DIRECTRIZ-ALTA-POR-ESCANEO F5
   =====================================================================
   Toda la lógica de la ficha vive aquí, en funciones puras: qué campos se
   ven según el tipo de código, de dónde salió cada dato y cómo cambia la
   marca al editarlo, qué aviso va arriba y qué se manda al guardar. La
   pantalla (`FichaPrevia.jsx`) solo pinta y llama. Así las pruebas
   ejercitan la regla de verdad, sin navegador.
   ===================================================================== */

import { formasGtin14 } from '../accesos/codigos';

export const ID_FICHA = 'alta-escaneo';
export const ID_AJUSTES = 'alta-escaneo-ajustes';

// Los campos de identidad, en el orden en que se ven. `libro: true` solo
// aparece con un ISBN (o si ya trae valor).
export const CAMPOS = [
  { campo: 'nombre', etiqueta: 'Nombre', tipo: 'texto', max: 200 },
  { campo: 'marca', etiqueta: 'Marca o editorial', tipo: 'texto', max: 120 },
  { campo: 'autores', etiqueta: 'Autor o autores', tipo: 'lista', libro: true },
  { campo: 'descripcion', etiqueta: 'Descripción', tipo: 'largo', max: 2000 },
  { campo: 'contenido', etiqueta: 'Contenido neto', tipo: 'numero' },
  { campo: 'unidad', etiqueta: 'Unidad del contenido', tipo: 'unidad' },
  { campo: 'anio', etiqueta: 'Año', tipo: 'entero', libro: true },
  { campo: 'paginas', etiqueta: 'Páginas', tipo: 'entero', libro: true },
  { campo: 'idioma', etiqueta: 'Idioma', tipo: 'texto', max: 12, libro: true },
  { campo: 'edicion', etiqueta: 'Edición', tipo: 'texto', max: 60, libro: true },
];
export const UNIDADES_CONTENIDO = ['G', 'KG', 'MG', 'ML', 'L', 'CL'];

const NOMBRES_FUENTE = {
  openlibrary: 'Open Library',
  googlebooks: 'Google Books',
  openfoodfacts: 'Open Food Facts',
  openbeautyfacts: 'Open Beauty Facts',
  openpetfoodfacts: 'Open Pet Food Facts',
  openproductsfacts: 'Open Products Facts',
  upcitemdb: 'UPCitemdb',
  catalogo_tego: 'el catálogo de TEGO',
  usuarios_tego: 'usuarios de TEGO',
};

export function nombreDeFuente(clave) {
  return NOMBRES_FUENTE[clave] || clave || '';
}

/** La marca discreta de cada campo: «de Open Library», «corregido por ti»… */
export function marcaDeOrigen(origen) {
  if (!origen) return '';
  if (origen === 'usuario') return 'corregido por ti';
  if (origen === 'usuarios_tego') return 'aportado por usuarios de TEGO';
  return `de ${nombreDeFuente(origen)}`;
}

const TIPOS = {
  ISBN: 'Libro', ISSN: 'Revista', GTIN_MX: 'Producto de México', GTIN: 'Producto',
  INTERNO: 'Código de uso interno', QR_TEGO: 'Código QR de TEGO', DESCONOCIDO: 'Código',
};

/** «Libro · ISBN 978-0-306-40615-7» */
export function tipoDetectado(lectura) {
  if (!lectura) return '';
  const t = TIPOS[lectura.tipo] || 'Código';
  // Sin guiones: dónde van depende del país y la editorial del libro.
  if (lectura.tipo === 'ISBN' && lectura.isbn13) return `${t} · ISBN ${lectura.isbn13}`;
  return t;
}

export function esLibro(lectura) {
  return lectura?.tipo === 'ISBN' || lectura?.tipo === 'ISSN';
}

function vacio(v) {
  return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
}

/** Los campos que se ven para este código (los de libro, solo si aplica o traen valor). */
export function camposVisibles(lectura, campos = {}) {
  return CAMPOS.filter((c) => !c.libro || esLibro(lectura) || !vacio(campos[c.campo]));
}

/**
 * El estado inicial de la ficha a partir de `GET /api/alta-escaneo/ficha`.
 * `valoresFuente` se congela: es contra lo que se compara al guardar para
 * saber qué corrigió la persona.
 */
export function estadoInicial(respuesta) {
  const ficha = respuesta?.ficha;
  const campos = { ...(ficha?.campos || {}) };
  return {
    lectura: respuesta?.lectura || null,
    campos,
    origen: { ...(ficha?.origen || {}) },
    valoresFuente: JSON.parse(JSON.stringify(campos)),
    imagen: ficha?.imagen?.id ? { id: ficha.imagen.id, fuente: ficha.imagen.fuente } : null,
    imagenNoSeTrajo: Boolean(ficha?.imagen?.no_se_pudo_traer),
    otras: respuesta?.otras || [],
    fuentes: respuesta?.fuentes || [],
    confianza: ficha?.confianza ?? 0,
    atribuciones: ficha?.atribuciones || [],
    encontrada: Boolean(ficha && !vacio(campos.nombre)),
    yaEsDe: respuesta?.ya_es_de || null,
  };
}

/** Editar un campo: el valor cambia y su marca pasa a «corregido por ti». */
export function cambiarCampo(estado, campo, valor) {
  return {
    ...estado,
    campos: { ...estado.campos, [campo]: valor },
    origen: { ...estado.origen, [campo]: 'usuario' },
  };
}

/** Usar el nombre de «Otras coincidencias». */
export function usarCoincidencia(estado, otra) {
  return {
    ...estado,
    campos: { ...estado.campos, nombre: otra.nombre },
    origen: { ...estado.origen, nombre: otra.fuente },
  };
}

/**
 * El aviso honesto de arriba, o null. Ningún «verificado»: las fuentes son
 * comunitarias o de terceros.
 */
export function avisoDeLaFicha(estado) {
  if (!estado.encontrada) {
    return {
      tono: 'atencion',
      texto: 'Este código no aparece en el catálogo ni en las fuentes consultadas. Llena los datos '
        + 'y la próxima vez TEGO ya lo conocerá.',
    };
  }
  if (estado.confianza >= 70) return null;
  if (estado.fuentes.length <= 1) {
    return { tono: 'atencion', texto: 'Revisa los datos: solo se encontró en una fuente.' };
  }
  return { tono: 'atencion', texto: 'Revisa los datos: las fuentes no coinciden en todo.' };
}

/** «Encontrado en Open Library y Google Books» */
export function dondeSeEncontro(estado) {
  const nombres = estado.fuentes.map(nombreDeFuente).filter(Boolean);
  if (!nombres.length) return '';
  const lista = nombres.length === 1 ? nombres[0]
    : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
  return `Encontrado en ${lista}`;
}

/** Lo que falta para poder guardar, o null. */
export function problemaParaGuardar(estado, empresa) {
  const nombre = String(estado.campos.nombre || '').trim();
  if (nombre.length < 2) return 'Escribe el nombre del producto.';
  const cantidad = Number(empresa.cantidad || 0);
  if (Number.isNaN(cantidad) || cantidad < 0) return 'La cantidad es un número de 0 en adelante.';
  if (cantidad > 0 && !empresa.ubicacion) return 'Elige en «Ubicación» dónde entra la mercancía, o pon la cantidad en 0.';
  return null;
}

function limpioParaEnviar(campos) {
  const salida = {};
  for (const { campo, tipo } of CAMPOS) {
    let v = campos[campo];
    if (vacio(v)) continue;
    if (tipo === 'numero') v = Number(v);
    if (tipo === 'entero') v = Number.parseInt(v, 10);
    if (tipo === 'lista' && typeof v === 'string') v = v.split(',').map((x) => x.trim()).filter(Boolean);
    if (typeof v === 'string') v = v.trim();
    if (vacio(v) || (typeof v === 'number' && Number.isNaN(v))) continue;
    salida[campo] = v;
  }
  return salida;
}

/** El cuerpo de `POST /api/alta-escaneo/guardar`. */
export function cuerpoParaGuardar(estado, empresa, { medio = 'CAMARA', esOtroProducto = false } = {}) {
  const campos = limpioParaEnviar(estado.campos);
  const origen = {};
  for (const k of Object.keys(campos)) origen[k] = estado.origen[k] || 'usuario';
  return {
    codigo: estado.lectura?.lectura,
    campos,
    origen_campos: origen,
    valores_fuente: limpioParaEnviar(estado.valoresFuente),
    sku: empresa.sku?.trim() || null,
    unidad_medida: empresa.unidad_medida || 'PIEZA',
    precio_venta: String(empresa.precio_venta || 0),
    costo: String(empresa.costo || 0),
    cantidad: String(empresa.cantidad ?? 1),
    ubicacion: empresa.ubicacion || null,
    id_imagen: estado.imagen?.id || null,
    medio,
    es_otro_producto: esOtroProducto,
  };
}

/** F8 · El aviso de lo dado de alta sin internet, o null si no hay nada. */
export function avisoDePropuestas(lista) {
  const n = lista.length;
  if (!n) return null;
  const conImagen = lista.filter((p) => p.id_imagen_propuesta).length;
  const que = conImagen === n ? (n === 1 ? 'la imagen y los datos' : 'las imágenes y los datos') : 'los datos';
  return `Encontramos ${que} de ${n} ${n === 1 ? 'producto' : 'productos'} que diste de alta sin internet.`;
}

/* ALTA-ESC (16 oct 2026) · EL ESCÁNER PRINCIPAL ABRE LA FICHA SOLO.
   Cuando `Escanear` recibe un 404 («ese código no es de ningún producto»),
   abre la Ficha previa sin pasar por un botón si:
     · quien escanea puede dar de alta (permiso `catalogo`, desde ENCARGADO:
       lo mismo que pide el menú de «Dar de alta escaneando»);
     · no está registrando una SALIDA ni un TRASPASO (lo que sale o se mueve
       ya tiene que existir: ahí el 404 se lee, no se convierte en alta);
     · y el código es un código de barras comercial con su dígito
       verificador correcto (EAN-8, EAN-13, UPC-A, UPC-E, GTIN-14), sin el
       prefijo 2 de uso interno de la tienda. Un SKU tecleado o una
       etiqueta interna sigue con «Darlo de alta» (U-03), como hasta hoy. */
export function abrirFichaSola(codigo, { modo = null, puedeAlta = false } = {}) {
  if (!puedeAlta || modo === 'SALIDA' || modo === 'TRASPASO') return false;
  return formasGtin14(codigo).some((f) => !f.replace(/^0+/, '').startsWith('2'));
}
